import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { TablePagination } from "../components/common/TablePagination";
import { extractErrorMessage, fetchResourceList } from "../lib/api";

const ACTION_OPTIONS = ["", "load_created", "load_updated", "load_deleted", "customer_reference_updated", "dispatch_reassigned", "stop_completed", "invoice_deleted"];
const ENTITY_OPTIONS = ["", "load", "dispatch_trip", "invoice"];

export default function AuditTrailPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const loadAuditTrail = async () => {
      setLoading(true);
      try {
        const payload = await fetchResourceList("/audit-trail", {
          page: currentPage,
          page_size: pageSize,
          search: search || undefined,
          entity_type: entityType || undefined,
          action: action || undefined,
        });
        setRows(payload.items || []);
        setTotalItems(payload.total_items || 0);
      } catch (error) {
        toast.error(extractErrorMessage(error, "Unable to load audit trail."));
      } finally {
        setLoading(false);
      }
    };
    loadAuditTrail();
  }, [action, currentPage, entityType, pageSize, search]);

  return (
    <div className="space-y-8" data-testid="audit-trail-page">
      <section className="panel-surface space-y-4 px-6 py-6">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-zinc-500">Admin controls</p>
          <h1 className="font-display text-4xl text-zinc-900">Audit Trail</h1>
          <p className="max-w-3xl text-sm text-zinc-600">Review recent operational changes across loads, dispatch reassignment, stop completion, and invoice reversals.</p>
        </div>

        <div className="grid gap-3 md:grid-cols-[1.4fr_0.7fr_0.9fr]">
          <label className="relative block" data-testid="audit-search-wrap">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input className="w-full rounded-2xl border border-zinc-200 bg-white px-11 py-3 text-sm text-zinc-800" data-testid="audit-search-input" onChange={(event) => { setCurrentPage(1); setSearch(event.target.value); }} placeholder="Search summary or load / invoice label" value={search} />
          </label>
          <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-medium text-zinc-800" data-testid="audit-entity-filter" onChange={(event) => { setCurrentPage(1); setEntityType(event.target.value); }} value={entityType}>
            <option value="">All entities</option>
            {ENTITY_OPTIONS.filter(Boolean).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-medium text-zinc-800" data-testid="audit-action-filter" onChange={(event) => { setCurrentPage(1); setAction(event.target.value); }} value={action}>
            <option value="">All actions</option>
            {ACTION_OPTIONS.filter(Boolean).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </div>
      </section>

      <section className="panel-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm" data-testid="audit-trail-table">
            <thead className="bg-zinc-100/80 text-xs uppercase tracking-[0.18em] text-zinc-500">
              <tr>
                <th className="px-5 py-4">When</th>
                <th className="px-5 py-4">Action</th>
                <th className="px-5 py-4">Entity</th>
                <th className="px-5 py-4">User</th>
                <th className="px-5 py-4">Summary</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-5 py-10 text-center text-zinc-500" colSpan={5} data-testid="audit-loading">Loading audit trail...</td></tr>
              ) : rows.length ? rows.map((row) => (
                <tr className="border-t border-zinc-100 align-top" data-testid={`audit-row-${row.id}`} key={row.id}>
                  <td className="px-5 py-4 text-zinc-600">{new Date(row.created_at).toLocaleString()}</td>
                  <td className="px-5 py-4"><span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-700" data-testid={`audit-action-${row.id}`}>{row.action}</span></td>
                  <td className="px-5 py-4 text-zinc-700">
                    <p className="font-semibold text-zinc-900">{row.entity_label || "—"}</p>
                    <p className="text-xs uppercase tracking-[0.16em] text-zinc-500">{row.entity_type}</p>
                  </td>
                  <td className="px-5 py-4 text-zinc-600">{row.created_by?.full_name || row.created_by?.email || "System"}</td>
                  <td className="px-5 py-4 text-zinc-700" data-testid={`audit-summary-${row.id}`}>{row.summary}</td>
                </tr>
              )) : (
                <tr><td className="px-5 py-10 text-center text-zinc-500" colSpan={5} data-testid="audit-empty">No audit events match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination currentPage={currentPage} pageSize={pageSize} setCurrentPage={setCurrentPage} setPageSize={setPageSize} testIdPrefix="audit-trail" totalItems={totalItems} />
      </section>
    </div>
  );
}